import { useSyncExternalStore } from 'react';

/**
 * THE READER'S TEXT SIZE for the Chart Analytica dashboard.
 *
 * <h2>A MULTIPLIER, not a set of three stylesheets</h2>
 *
 * Every size in that panel is deliberate relative to its neighbours — a section heading is
 * smaller than a summary card's figure, a caveat smaller than the prose it qualifies, and a
 * table cell sits between them. Three hand-written size sets would be three chances for that
 * hierarchy to drift apart, and the drift would only show at one of the three settings.
 *
 * <p>So there is ONE set of sizes and a factor over it. Changing a size changes it at every
 * setting, which is what keeps the three legible as the same design.
 *
 * <h2>MEDIUM is 1.1, so the default is already a little larger than it shipped</h2>
 *
 * The panel was built at what is now SMALL, and that density was reported as hard to read.
 * Medium is the new default rather than a new maximum — "slightly larger baseline" and "a
 * control" are the same change made once. SMALL is kept because somebody fitting two runs side
 * by side on a laptop wants the density back, and removing it would make the comparison view
 * worse to fix the single view.
 *
 * <h2>Why a store rather than a prop</h2>
 *
 * The scale is read by a dozen module-local components inside the dialog — the section, the
 * table, the cards, the caveat — several of them nested two deep. The same argument
 * `glassTransparency` makes: threading it is a dozen signatures and a dozen chances to forget
 * one, and a component left at the default would be a panel in two sizes.
 *
 * <p>PERSISTED, because it is a reading preference and not a session setting: a reader who
 * needs larger text should not have to say so again tomorrow. Every `localStorage` touch is
 * wrapped — Safari in private mode throws on `setItem`, and a panel that cannot REMEMBER a size
 * must still be able to CHANGE one.
 */

/** The one key, namespaced like the toolbar's own preference so it is findable in devtools. */
export const ANALYTICA_TEXT_SCALE_KEY = 'chart_analytica_text_scale';

/**
 * The three settings.
 *
 * <p>SMALL is 1.0 — the density the panel shipped at, kept reachable. LARGE is 1.3 rather than
 * something bolder because the panel has a fixed width: past about a third the table columns
 * start wrapping, and a wrapped numeric column is harder to read than a small one.
 */
export const TEXT_SCALES = [
  { key: 'small', label: 'S', factor: 1.0, hint: 'The densest setting — most rows on screen.' },
  { key: 'medium', label: 'M', factor: 1.1, hint: 'The default.' },
  { key: 'large', label: 'L', factor: 1.3, hint: 'Largest that keeps the tables unwrapped.' },
];

export const DEFAULT_TEXT_SCALE = 'medium';

const keyOf = (k) => (TEXT_SCALES.some(s => s.key === k) ? k : DEFAULT_TEXT_SCALE);

const read = () => {
  try {
    return keyOf(window.localStorage.getItem(ANALYTICA_TEXT_SCALE_KEY));
  } catch (e) {
    return DEFAULT_TEXT_SCALE;
  }
};

let value = read();
const listeners = new Set();

/** The current setting's key. */
export const textScaleKey = () => value;

/** The current setting's multiplier. */
export const textScaleFactor = () =>
  (TEXT_SCALES.find(s => s.key === value) || TEXT_SCALES[1]).factor;

/**
 * Set it, persist it, and tell every part of the panel at once.
 *
 * <p>An unknown key falls back to the default rather than being stored: a typo'd value written
 * to `localStorage` would survive every reload and the panel would silently render at a size
 * nobody chose.
 */
export function setTextScale(next) {
  const wanted = keyOf(next);
  if (wanted === value) return;
  value = wanted;
  try {
    window.localStorage.setItem(ANALYTICA_TEXT_SCALE_KEY, wanted);
  } catch (e) {
    // The setting still applies to this session; only remembering it failed.
  }
  listeners.forEach((fn) => fn());
}

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Re-reads `localStorage`. For tests, which need a known starting point per case. */
export function resetTextScaleForTest() {
  value = read();
  listeners.forEach((fn) => fn());
}

/** Subscribe a component to the setting. */
export function useTextScale() {
  return useSyncExternalStore(subscribe, textScaleKey, textScaleKey);
}

/**
 * A size function for the current setting: `fs(11)` is eleven points at SMALL and twelve at
 * MEDIUM.
 *
 * <p>Rounded to one decimal, because MUI emits the number into a `px` string and an unrounded
 * 11.000000000000002 reaches the DOM verbatim.
 */
export function useFontSizer() {
  const key = useTextScale();
  const factor = (TEXT_SCALES.find(s => s.key === key) || TEXT_SCALES[1]).factor;
  return (base) => Math.round(base * factor * 10) / 10;
}

export default useTextScale;
